/** Même valeur que `DEFAULT_SHAPE_TEXT_COLOR` de `MapLibreAdapter` : la couleur qu'un texte prend
 * réellement quand rien ne la définit. */
export const DEFAULT_TEXT_COLOR = '#000000';

export type PreviewLetterTextStyle = {
	color?: string;
	bgColor?: string;
};

/**
 * Lettre-spécimen d'un aperçu de style : elle ne représente pas un contenu réel (un thème n'a pas de
 * texte, une forme peut n'en avoir aucun) mais sert à montrer la couleur du texte et son halo.
 *
 * Partagée entre `ThemeSwatch` et `ShapeSwatch` : les deux subtilités ci-dessous (halo peint sous le
 * remplissage, correction optique) se sont chacune payées d'un aller-retour, autant ne pas les
 * dupliquer.
 *
 * Le composant pose la typographie ET les couleurs de texte : l'aperçu qui l'englobe n'a plus qu'à
 * s'occuper du fond et de la bordure.
 */
export default function PreviewLetter({ letter, text, size }: {
	letter: string;
	text: PreviewLetterTextStyle | undefined;
	/** Côté de l'aperçu englobant — la taille de la lettre et son halo s'en déduisent. */
	size: number;
}) {
	const fontSize = Math.round(size * 0.6);

	/**
	 * `bgColor` est rendu en HALO autour du texte (`text-halo-color`/`text-halo-width` dans
	 * `MapLibreAdapter`), pas en rectangle plein derrière lui : l'équivalent CSS est un contour peint
	 * SOUS le remplissage (`paint-order`), sinon il rongerait la lettre au lieu de l'entourer.
	 *
	 * Proportionnel à l'aperçu, là où la carte utilise un `TEXT_HALO_WIDTH_PX` fixe sur un label bien
	 * plus grand.
	 */
	const haloWidth = text?.bgColor ? Math.max(1, Math.round(size / 9)) : 0;

	/**
	 * Correction optique vers le BAS. Flexbox centre la boîte de ligne, pas l'encre : avec
	 * `lineHeight: 1` cette boîte fait 1 em, la ligne de base est à ~0,80 em du haut et une capitale
	 * monte de ~0,71 em au-dessus — son encre occupe donc 0,09 à 0,80 em, centrée sur 0,445 em au lieu
	 * de 0,50. L'espace des jambages, en bas, n'est jamais occupé par une majuscule, d'où une lettre
	 * qui paraît trop haute.
	 *
	 * Le coefficient dépend des métriques de la police d'interface : à réajuster si elle change.
	 */
	const opticalOffset = fontSize * 0.055;

	return (
		<span
			style={{
				fontSize,
				fontWeight: 'bold',
				lineHeight: 1,
				color: text?.color || DEFAULT_TEXT_COLOR,
				transform: `translateY(${opticalOffset}px)`,
				...(haloWidth ? {
					WebkitTextStrokeWidth: `${haloWidth}px`,
					WebkitTextStrokeColor: text?.bgColor,
					paintOrder: 'stroke fill',
				} : {}),
			}}
		>
			{letter}
		</span>
	);
}
